import { cn } from '@/lib/utils';

export interface ProgressRingProps {
  /** 0-100. Clamped, so callers can pass raw percentages. */
  pct: number;
  /** Outer diameter in px. Stroke scales with it, so a 14px ring and a 24px
   *  ring read as the same object at two sizes. */
  size?: number;
  /**
   * Arc colour, as a CSS value. Pass a token: `var(--ds-brand-subtle)`.
   * Defaults to brand, because a completion ring is DATA. `--ds-success-*` is
   * the generic kelly green reserved for real status badges and toasts, and
   * reading "good" as "success" is what put a neon bar on the member cards.
   */
  stroke?: string;
  /** Track colour behind the arc. */
  track?: string;
  className?: string;
}

/**
 * Circular progress ring: a track plus one arc filled to `pct`.
 *
 * A ring instead of a horizontal bar wherever the number is a completion
 * fraction sitting in a dense row. A 64px bar spends its width saying what the
 * adjacent "91%" already said, and at a glance a nearly-full bar and a full one
 * are the same shape; a ring reads as an at-a-glance dial, costs ~16px, and
 * leaves the row to the label.
 *
 * `BudgetRing` (`BudgetTab/budgetTone.tsx`) and `MiniBudgetRing`
 * (`EngineerCapsTable.tsx`) are the same drawing, hand-rolled twice before this
 * existed. New callers should use this one.
 */
export default function ProgressRing({
  pct,
  size = 16,
  stroke = 'var(--ds-brand-subtle)',
  track = 'var(--ds-border-subtler)',
  className,
}: ProgressRingProps) {
  const center = size / 2;
  const strokeWidth = size / 7.2;
  const r = center - strokeWidth / 2 - 0.5;
  const circumference = 2 * Math.PI * r;
  const clamped = Math.min(Math.max(pct, 0), 100);
  const dash = (clamped / 100) * circumference;
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className={cn('-rotate-90 shrink-0', className)}
      aria-hidden="true"
    >
      <circle
        cx={center}
        cy={center}
        r={r}
        fill="none"
        strokeWidth={strokeWidth}
        style={{ stroke: track }}
      />
      <circle
        cx={center}
        cy={center}
        r={r}
        fill="none"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeDasharray={`${dash} ${circumference}`}
        style={{ stroke }}
        className="transition-[stroke-dasharray] duration-300 ease-out"
      />
    </svg>
  );
}
